import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Eye, EyeOff, ArrowLeft, UserPlus } from 'lucide-react';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    name: '',
    surname: '',
    role: 'atleta'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Show nothing while redirecting
  if (user) {
    return null;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.email !== formData.confirmEmail) {
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      return;
    }
    
    if (formData.password.length < 6) {
      return;
    }
    
    setLoading(true);
    
    try {
      const { confirmPassword, confirmEmail, ...registerData } = formData;
      const result = await register(registerData);
      if (result.success) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const isPasswordValid = formData.password.length >= 6;
  const doPasswordsMatch = formData.password === formData.confirmPassword;
  const doEmailsMatch = formData.email === formData.confirmEmail;

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 via-white to-red-50">
      <div className="max-w-md w-full space-y-8 animate-fadeIn">
        {/* Back to home */}
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-gray-600 hover:text-red-600 transition-colors mb-4"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna alla home
        </Link>

        <Card className="shadow-xl border-0 glass" data-testid="register-card">
          <CardHeader className="text-center pb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="text-white w-8 h-8" />
            </div>
            <CardTitle className="text-3xl font-display font-bold text-gray-900" data-testid="register-title">
              Unisciti a Dance Academy
            </CardTitle>
            <CardDescription className="text-gray-600" data-testid="register-subtitle">
              Crea il tuo account per iniziare la tua formazione
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                    Nome *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="custom-focus"
                    placeholder="Mario"
                    data-testid="name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="surname" className="text-sm font-medium text-gray-700">
                    Cognome *
                  </Label>
                  <Input
                    id="surname"
                    name="surname"
                    type="text"
                    required
                    value={formData.surname}
                    onChange={handleChange}
                    className="custom-focus"
                    placeholder="Rossi"
                    data-testid="surname-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="custom-focus"
                  placeholder="mario.rossi@esempio.com"
                  data-testid="email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmEmail" className="text-sm font-medium text-gray-700">
                  Conferma Email *
                </Label>
                <Input
                  id="confirmEmail"
                  name="confirmEmail"
                  type="email"
                  required
                  value={formData.confirmEmail}
                  onChange={handleChange}
                  className={`custom-focus ${formData.confirmEmail && !doEmailsMatch ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="mario.rossi@esempio.com"
                  data-testid="confirm-email-input"
                />
                {formData.confirmEmail && !doEmailsMatch && (
                  <p className="text-xs text-red-600 mt-1" data-testid="email-mismatch-error">
                    Le email non corrispondono
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`custom-focus pr-10 ${
                      formData.password && !isPasswordValid ? 'border-red-500' : ''
                    }`}
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    data-testid="toggle-password-button"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.password && !isPasswordValid && (
                  <p className="text-xs text-red-600" data-testid="password-error">
                    La password deve essere di almeno 6 caratteri
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Conferma Password *
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`custom-focus ${
                    formData.confirmPassword && !doPasswordsMatch ? 'border-red-500' : ''
                  }`}
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
                {formData.confirmPassword && !doPasswordsMatch && (
                  <p className="text-xs text-red-600" data-testid="confirm-password-error">
                    Le password non corrispondono
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role" className="text-sm font-medium text-gray-700">
                  Tipo di Registrazione *
                </Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  data-testid="role-select"
                >
                  <SelectTrigger id="role" className="custom-focus">
                    <SelectValue placeholder="Seleziona tipo di registrazione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="atleta" data-testid="role-option-atleta">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                        <span className="font-medium">ATLETA</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="teacher" data-testid="role-option-teacher">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                        <span className="font-medium">INSEGNANTE</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Seleziona il tipo di account che desideri creare
                </p>
              </div>
              
              <Button
                type="submit"
                disabled={loading || !isPasswordValid || !doPasswordsMatch || !doEmailsMatch}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit-button"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Registrazione in corso...
                  </div>
                ) : (
                  'Registrati'
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Hai già un account?{' '}
                <Link 
                  to="/login" 
                  className="font-medium text-red-600 hover:text-red-700 transition-colors"
                  data-testid="login-link"
                >
                  Accedi qui
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Info card */}
        <Card className="bg-blue-50 border-blue-200" data-testid="info-card">
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">Procedura di Registrazione:</h3>
            <div className="space-y-1 text-xs text-blue-700">
              <div><strong>1.</strong> Completa la registrazione con i tuoi dati</div>
              <div><strong>2.</strong> Attendi l'approvazione dell'amministratore</div>
              <div><strong>3.</strong> L'admin ti assegnerà il ruolo appropriato (Atleta/Teacher)</div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              * Tutti gli account devono essere approvati e il ruolo assegnato dall'amministratore
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
